import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, ArrowRight, Upload, ListChecks, BookOpen, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';

const DISMISS_KEY = 'reconciliation_wizard_dismissed_at';
const SNOOZE_DAYS = 14;

const STEPS = [
  { key: 'upload', icon: Upload, route: '/bank-statements', titleKey: 'wiz_upload_title', descKey: 'wiz_upload_desc', ctaKey: 'wiz_upload_cta' },
  { key: 'review', icon: ListChecks, route: '/bank-statements', titleKey: 'wiz_review_title', descKey: 'wiz_review_desc', ctaKey: 'wiz_review_cta' },
  { key: 'narrate', icon: BookOpen, route: '/bank-match-audit', titleKey: 'wiz_narrate_title', descKey: 'wiz_narrate_desc', ctaKey: 'wiz_narrate_cta' },
];

/**
 * Multi-step "Reconciliation 0→1" wizard. Replaces the static idle nudge.
 * Shows three sequential cards. Completed steps are highlighted green with a
 * check; the next pending step is the call-to-action.
 *
 * Backend probe (`/bank-matching/onboarding-status`) drives completion:
 *   step 1 done → has_statements
 *   step 2 done → has_audit_entries
 *   step 3 done → has_narrative
 * The whole wizard hides itself once all three are complete OR the user dismisses.
 */
export const ReconciliationNudge = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (ts) {
      const ms = Date.now() - Number(ts);
      if (ms < SNOOZE_DAYS * 24 * 60 * 60 * 1000) { setDismissed(true); return; }
    }
    api.get('/bank-matching/onboarding-status').then(r => setStatus(r.data || null)).catch(() => {});
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  if (dismissed || !status) return null;
  const completed = {
    upload: !!status.has_statements,
    review: !!status.has_audit_entries,
    narrate: !!status.has_narrative,
  };
  // Hide once everything is done, OR when only step 3 is missing AND tenant is mature
  // (we don't want to force narrate generation on every active tenant).
  const allDone = completed.upload && completed.review && completed.narrate;
  if (allDone) return null;
  // Hide when tenant is brand-new (< 7 days) — same threshold as old nudge.
  if ((status.tenant_age_days ?? 999) < 7) return null;

  const firstPending = STEPS.find(s => !completed[s.key]) || STEPS[0];

  return (
    <div
      className="relative rounded-2xl border border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 dark:from-violet-950/30 dark:via-fuchsia-950/30 dark:to-pink-950/30 p-4 sm:p-5"
      data-testid="reconciliation-wizard"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-violet-100 dark:hover:bg-violet-900/40 flex items-center justify-center transition-colors"
        data-testid="reconciliation-wizard-dismiss"
        aria-label="Dismiss"
      >
        <X size={13} className="text-violet-700 dark:text-violet-300" />
      </button>

      <div className="flex items-start gap-3 mb-4 pr-7">
        <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center flex-shrink-0">
          <Sparkles size={18} className="text-violet-600 dark:text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-violet-900 dark:text-violet-200">
            {t('wiz_title')}
          </p>
          <p className="text-xs text-violet-800/80 dark:text-violet-300/80 mt-0.5">
            {t('wiz_subtitle')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = completed[s.key];
          const isNext = !done && s.key === firstPending.key;
          return (
            <div
              key={s.key}
              className={`relative rounded-xl border p-3 transition-all ${
                done
                  ? 'bg-emerald-50/70 border-emerald-200 dark:bg-emerald-900/15 dark:border-emerald-800'
                  : isNext
                    ? 'bg-white/80 border-violet-300 shadow-sm dark:bg-stone-900/60 dark:border-violet-700'
                    : 'bg-white/40 border-violet-100 dark:bg-stone-900/30 dark:border-violet-900'
              }`}
              data-testid={`wizard-step-${s.key}`}
            >
              <div className="flex items-start gap-2.5">
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    done
                      ? 'bg-emerald-500 text-white'
                      : isNext
                        ? 'bg-violet-600 text-white'
                        : 'bg-violet-100 text-violet-600 dark:bg-violet-900/40'
                  }`}
                >
                  {done ? <Check size={15} /> : <Icon size={15} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-violet-500 dark:text-violet-400">
                      {t('wiz_step')} {i + 1}
                    </span>
                    {done && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                        {t('wiz_done')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-bold mt-0.5 text-violet-900 dark:text-violet-100">{t(s.titleKey)}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{t(s.descKey)}</p>
                  {!done && (
                    <Button
                      size="sm"
                      variant={isNext ? 'default' : 'ghost'}
                      onClick={() => navigate(s.route)}
                      className={`mt-2 h-6 text-[10px] ${isNext ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}`}
                      data-testid={`wizard-step-${s.key}-cta`}
                    >
                      {t(s.ctaKey)} <ArrowRight size={9} className="ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
